import { useMemo, useState } from "react";
import { ArrowUpRight, Minus, Plus } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { goToAudit } from "@/lib/scroll";

const VALUE_CHIPS = [100, 250, 500, 1000, 5000];

export const Calculator = () => {
  const [value, setValue] = useState("500");
  const [customers, setCustomers] = useState(10);

  const monthly = useMemo(() => {
    const v = parseFloat(value.replace(/[^0-9.]/g, "")) || 0;
    return v * customers;
  }, [value, customers]);

  return (
    <section className="relative overflow-hidden py-12 sm:py-16">
      <div className="pointer-events-none absolute left-[-10%] top-1/4 h-[400px] w-[400px] rounded-full bg-purple-600/12 blur-[140px]" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Do The Math</Eyebrow>
          <h2 className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-white sm:text-5xl lg:text-6xl">
            What Would More Customers Be{" "}
            <span className="text-purple-500">Worth To Your Business?</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="revenue-calculator"
            className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-[#272732] bg-[#272732] lg:grid-cols-2"
          >
            <div className="space-y-10 bg-[#121217] p-8 sm:p-12">
              <div>
                <label htmlFor="calc-customer-value" className="block text-sm font-bold text-zinc-300">
                  What is one new customer worth to you?
                </label>
                <div className="mt-3 flex items-center rounded-2xl border border-[#272732] bg-[#0B0B10] px-5 focus-within:border-purple-500">
                  <span className="font-display text-2xl font-extrabold text-purple-400">$</span>
                  <input
                    id="calc-customer-value"
                    data-testid="calc-customer-value"
                    inputMode="numeric"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="h-16 w-full bg-transparent px-3 font-display text-2xl font-extrabold text-white focus:outline-none"
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {VALUE_CHIPS.map((v) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`calc-value-chip-${v}`}
                      onClick={() => setValue(String(v))}
                      className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${
                        value === String(v)
                          ? "border-purple-500 bg-purple-600 text-white"
                          : "border-white/10 bg-white/5 text-zinc-400 hover:border-purple-500/40"
                      }`}
                    >
                      ${v.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-zinc-300">
                  How many additional customers would you like each month?
                </label>
                <div className="mt-3 flex items-center gap-4">
                  <button
                    type="button"
                    data-testid="calc-customers-minus"
                    aria-label="Fewer customers"
                    onClick={() => setCustomers((c) => Math.max(1, c - 1))}
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-colors hover:border-purple-500/50"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <div
                    data-testid="calc-customers-count"
                    className="flex h-16 flex-1 items-center justify-center rounded-2xl border border-[#272732] bg-[#0B0B10] font-display text-3xl font-extrabold text-white"
                  >
                    {customers}
                  </div>
                  <button
                    type="button"
                    data-testid="calc-customers-plus"
                    aria-label="More customers"
                    onClick={() => setCustomers((c) => Math.min(500, c + 1))}
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-colors hover:border-purple-500/50"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center bg-purple-600 p-8 text-white sm:p-12">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-purple-200">
                Potential New Revenue
              </p>
              <p
                data-testid="calc-monthly-revenue"
                className="mt-4 font-display text-6xl font-extrabold leading-none tracking-tighter sm:text-7xl"
              >
                ${monthly.toLocaleString()}
                <span className="mt-1 block text-2xl font-bold tracking-tight text-purple-200">/ month</span>
              </p>
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-purple-100">
                Now let's build a strategy to go get them.
              </p>
              <button
                type="button"
                data-testid="calc-build-plan-cta"
                onClick={goToAudit}
                className="group mt-8 flex w-fit items-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-wide text-purple-700 transition-colors duration-300 hover:bg-purple-100"
              >
                Build My Customer Plan
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <p className="mt-6 text-[11px] leading-relaxed text-purple-200/80">
                Illustrative calculation only. Not a guarantee or projection of actual results.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
